import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles } from '@material-ui/core/styles';

import Icon from '@mdi/react';

import { pantonFonts } from 'components/theme/common';
import { getRedesignDialogColors } from './redesignDialogColors';

const useStyles = makeStyles(
  (theme) => {
    const colors = getRedesignDialogColors(theme);
    return {
      // A 24px pill, its label in Panton at the size of the house chips.
      root: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
        boxSizing: 'border-box',
        maxWidth: '100%',
        height: 24,
        padding: '0 10px',
        borderRadius: 12,
        fontFamily: pantonFonts.panton,
        fontSize: '0.75rem',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      },
      withIcon: {
        paddingLeft: 7,
      },
      icon: {
        flexShrink: 0,
      },
      label: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      success: {
        backgroundColor: colors.successFill,
        color: colors.success,
      },
      warning: {
        backgroundColor: colors.warningFill,
        color: colors.warning,
      },
      danger: {
        backgroundColor: colors.dangerFill,
        color: colors.danger,
      },
      neutral: {
        backgroundColor: colors.selectedFill,
        color: colors.muted,
      },
    };
  },
  { name: 'LixRedesignDialogStatus' }
);

const TONES = ['success', 'warning', 'danger', 'neutral'];

/**
 * A small status chip for a redesigned dialog's header (its `leading`) or a
 * section: the status colour of `tone` on its tinted fill — `'success'`,
 * `'warning'`, `'danger'` — or the neutral grey; an optional @mdi/js `icon`
 * before the `label`. Other props (title, data-*) go to the chip.
 * @example
 * <RedesignDialogStatus tone="warning" icon={mdiClockOutline} label={t('expense.not_approved')} />
 * @param {Object} props
 * @param {'success'|'warning'|'danger'|'neutral'} [props.tone='neutral']
 * @param {string} [props.icon] an @mdi/js path
 * @param {React.ReactNode} props.label
 */
function RedesignDialogStatus({ tone, icon, label, className, ...rest }) {
  const classes = useStyles();
  const look = TONES.includes(tone) ? tone : 'neutral';
  return (
    <span
      className={clsx(
        classes.root,
        classes[look],
        icon && classes.withIcon,
        className
      )}
      data-redesign-dialog-status={look}
      {...rest}
    >
      {icon ? <Icon path={icon} size={0.667} className={classes.icon} /> : null}
      <span className={classes.label}>{label}</span>
    </span>
  );
}

RedesignDialogStatus.propTypes = {
  tone: PropTypes.oneOf(TONES),
  /** An @mdi/js path before the label. */
  icon: PropTypes.string,
  label: PropTypes.node.isRequired,
  className: PropTypes.string,
};

RedesignDialogStatus.defaultProps = {
  tone: 'neutral',
};

export default RedesignDialogStatus;
